<template>
  <v-dialog v-model="dialog" max-width="1400px" persistent scrollable>
    <v-card>
      <v-toolbar density="compact" color="deep-purple" dark>
        <v-toolbar-title class="text-subtitle-1">
          <v-icon size="small">mdi-history</v-icon>
          {{ $t('projects.history.title') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-switch
          v-if="!loading && history && history.length > 0 && currentIndex > 0"
          v-model="showComparison"
          :label="$t('projects.history.showChanges')"
          color="white"
          hide-details
          density="compact"
          class="mr-4"
        ></v-switch>
        <v-switch
          v-if="!loading && history && history.length > 0 && showComparison && currentIndex > 0"
          v-model="showOnlyChanged"
          :label="$t('projects.history.showOnlyChanged')"
          color="white"
          hide-details
          density="compact"
          class="mr-4"
        ></v-switch>
        <v-switch
          v-if="!loading && history && history.length > 0"
          v-model="hideEmpty"
          :label="$t('projects.history.hideEmpty')"
          color="white"
          hide-details
          density="compact"
          class="mr-4"
        ></v-switch>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-2" style="height: 75vh;">
        <!-- Loading State -->
        <div v-if="loading" class="d-flex justify-center align-center" style="height: 100%;">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <!-- No History State -->
        <div v-else-if="!history || history.length === 0" class="d-flex flex-column justify-center align-center" style="height: 100%;">
          <v-icon size="48" color="grey">mdi-history</v-icon>
          <p class="text-subtitle-1 mt-2 text-grey">{{ $t('projects.history.noHistory') }}</p>
        </div>

        <!-- History Navigation -->
        <div v-else>
          <!-- Navigation Controls -->
          <v-card outlined class="mb-2">
            <v-card-text class="py-2">
              <div class="d-flex align-center justify-space-between">
                <v-btn
                  icon
                  @click="navigateToPrevious"
                  :disabled="currentIndex === 0"
                  size="small"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>

                <div class="text-center flex-grow-1">
                  <div class="text-subtitle-2">
                    {{ $t('projects.history.version') }} {{ currentIndex + 1 }} / {{ history.length }}
                    <v-chip 
                      v-if="currentIndex === history.length - 1" 
                      size="x-small" 
                      color="success" 
                      class="ml-2"
                    >
                      {{ $t('projects.history.current') }}
                    </v-chip>
                  </div>
                  <div class="text-caption text-grey">
                    {{ formatDateTime(currentHistoryEntry.timestamp) }}
                  </div>
                  <v-chip size="x-small" color="primary" class="mt-1">
                    <v-icon size="x-small">mdi-account</v-icon>
                    {{ currentHistoryEntry.modifiedBy }}
                  </v-chip>
                </div>

                <v-btn
                  icon
                  @click="navigateToNext"
                  :disabled="currentIndex === history.length - 1"
                  size="small"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>

              <!-- Timeline -->
              <v-slider
                v-model="currentIndex"
                :min="0"
                :max="history.length - 1"
                :step="1"
                show-ticks="always"
                tick-size="3"
                class="mt-2"
                color="deep-purple"
                track-color="grey-lighten-2"
                density="compact"
              ></v-slider>
            </v-card-text>
          </v-card>

          <!-- Restore Action Card (staged per-field restores) -->
          <v-card outlined class="mb-2" v-if="pendingCount > 0" color="warning-lighten-5">
            <v-card-text class="py-2">
              <v-row align="center">
                <v-col cols="12" md="7">
                  <div class="text-subtitle-2 font-weight-bold mb-1">
                    <v-icon size="small" color="warning" class="mr-2">mdi-restore</v-icon>
                    {{ $t('projects.history.restoreSelected', { count: pendingCount }) }}
                  </div>
                  <div>
                    <v-chip
                      v-for="(value, fieldName) in pendingRestores"
                      :key="fieldName"
                      size="small"
                      color="warning"
                      class="mr-2 mb-1"
                      closable
                      @click:close="unstageRestore(fieldName)"
                    >
                      {{ getFieldLabel(fieldName) }}: {{ formatFieldValue(fieldName, value) }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" md="5" class="text-right">
                  <v-btn
                    variant="text"
                    @click="clearPending"
                    class="mr-2"
                    :disabled="restoring"
                  >
                    {{ $t('common.cancel') }}
                  </v-btn>
                  <v-btn
                    color="warning"
                    @click="confirmRestore"
                    prepend-icon="mdi-restore"
                    :loading="restoring"
                  >
                    {{ $t('projects.history.confirmRestore') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Fields Display (grouped) -->
          <template v-if="Object.keys(visibleGroups).length > 0">
            <v-card
              v-for="(group, groupName) in visibleGroups"
              :key="groupName"
              outlined
              class="mb-2"
            >
              <v-card-title :class="`bg-${group.color}-lighten-4 text-body-1 py-1`">
                {{ group.description }}
              </v-card-title>
              <v-card-text class="pt-2 pb-2">
                <v-row>
                <v-col
                  v-for="(field, fieldName) in group.fields"
                  :key="fieldName"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                >
                  <div
                    :class="{
                      'history-field': true,
                      'field-changed': showComparison && isFieldChanged(fieldName),
                      'field-pending': isPending(fieldName),
                      'pa-2': true,
                      'rounded': true
                    }"
                  >
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="text-caption text-grey">
                        {{ getFieldLabel(fieldName) }}
                        <v-chip
                          v-if="showComparison && isFieldChanged(fieldName)"
                          size="x-small"
                          color="warning"
                          class="ml-2"
                        >
                          {{ $t('projects.history.modified') }}
                        </v-chip>
                        <v-chip
                          v-if="isPending(fieldName)"
                          size="x-small"
                          color="deep-orange"
                          class="ml-2"
                        >
                          {{ $t('projects.history.pendingRestore') }}
                        </v-chip>
                      </div>

                      <!-- Per-field value timeline / restore menu -->
                      <v-menu v-if="canRestoreField(fieldName)" location="bottom end" :close-on-content-click="false">
                        <template v-slot:activator="{ props: menuProps }">
                          <v-btn
                            v-bind="menuProps"
                            icon
                            size="x-small"
                            variant="text"
                            :color="isPending(fieldName) ? 'deep-orange' : 'grey'"
                            :title="$t('projects.history.valueTimeline')"
                          >
                            <v-icon size="small">mdi-history</v-icon>
                          </v-btn>
                        </template>
                        <v-card min-width="280" max-width="420">
                          <v-card-title class="text-caption text-grey py-2">
                            {{ getFieldLabel(fieldName) }} — {{ $t('projects.history.valueTimeline') }}
                          </v-card-title>
                          <v-divider></v-divider>
                          <v-list density="compact" class="py-0">
                            <v-list-item
                              v-for="(item, idx) in fieldValueHistory(fieldName)"
                              :key="idx"
                              :active="isPending(fieldName) && JSON.stringify(pendingRestores[fieldName]) === JSON.stringify(item.value)"
                            >
                              <v-list-item-title class="text-body-2">
                                {{ formatFieldValue(fieldName, item.value) }}
                                <v-chip v-if="item.isCurrent" size="x-small" color="success" class="ml-2">
                                  {{ $t('projects.history.current') }}
                                </v-chip>
                              </v-list-item-title>
                              <v-list-item-subtitle class="text-caption">
                                {{ formatDateTime(item.timestamp) }} · {{ item.modifiedBy }}
                              </v-list-item-subtitle>
                              <template v-slot:append>
                                <v-btn
                                  v-if="!item.isCurrent"
                                  size="x-small"
                                  variant="tonal"
                                  color="warning"
                                  prepend-icon="mdi-restore"
                                  @click="stageRestore(fieldName, item.value)"
                                >
                                  {{ $t('projects.history.restore') }}
                                </v-btn>
                              </template>
                            </v-list-item>
                          </v-list>
                          <template v-if="!isLiveEmpty(fieldName)">
                            <v-divider></v-divider>
                            <v-card-actions class="py-1">
                              <v-btn
                                size="small"
                                variant="text"
                                color="error"
                                prepend-icon="mdi-close-circle-outline"
                                :disabled="isClearStaged(fieldName)"
                                @click="stageClear(fieldName)"
                              >
                                {{ $t('projects.history.clearField') }}
                              </v-btn>
                            </v-card-actions>
                          </template>
                        </v-card>
                      </v-menu>
                    </div>
                    <div class="text-body-2">
                      <!-- Special rendering for notes field -->
                      <span v-if="isNotesField(fieldName)">
                        <div v-if="Array.isArray(getCurrentValue(fieldName)) && getCurrentValue(fieldName).length > 0">
                          <v-list dense class="notes-list pa-0">
                            <v-list-item
                              v-for="(note, idx) in getCurrentValue(fieldName)"
                              :key="idx"
                              class="note-item px-2 py-1"
                            >
                              <v-list-item-title class="text-body-2">
                                <v-chip size="x-small" :color="getStatusColor(note.statusId)" class="mr-2">
                                  {{ note.statusDescription || `Status ${note.statusId}` }}
                                </v-chip>
                                {{ note.text }}
                              </v-list-item-title>
                              <v-list-item-subtitle class="text-caption">
                                {{ note.user }} - {{ formatNoteDate(note.timestamp) }}
                              </v-list-item-subtitle>
                            </v-list-item>
                          </v-list>
                        </div>
                        <span v-else class="text-grey">{{ $t('projects.history.empty') }}</span>
                      </span>
                      <!-- Standard comparison view for other fields -->
                      <span v-else-if="showComparison && isFieldChanged(fieldName)">
                        <span class="old-value text-decoration-line-through text-grey">
                          {{ formatFieldValue(fieldName, getPreviousValue(fieldName)) }}
                        </span>
                        <v-icon size="small" class="mx-2">mdi-arrow-right</v-icon>
                        <span class="new-value font-weight-bold">
                          {{ formatFieldValue(fieldName, getCurrentValue(fieldName)) }}
                        </span>
                      </span>
                      <span v-else>
                        {{ formatFieldValue(fieldName, getCurrentValue(fieldName)) }}
                      </span>
                    </div>
                  </div>
                </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </template>

          <!-- Empty state when the active filters hide every field -->
          <v-card v-else outlined>
            <v-card-text class="text-center text-grey py-8">
              <v-icon size="32" color="grey">mdi-filter-off</v-icon>
              <div class="mt-2">{{ $t('projects.history.noFieldsToShow') }}</div>
            </v-card-text>
          </v-card>

          <!-- Changed Fields Summary -->
          <v-card outlined class="mt-4" v-if="showComparison && changedFieldsCount > 0">
            <v-card-title class="text-subtitle-1">
              <v-icon left color="warning">mdi-alert-circle</v-icon>
              {{ $t('projects.history.changedFields') }}: {{ changedFieldsCount }}
            </v-card-title>
            <v-card-text>
              <v-chip
                v-for="fieldName in changedFieldsList"
                :key="fieldName"
                size="small"
                color="warning"
                class="mr-2 mb-2"
              >
                {{ getFieldLabel(fieldName) }}
              </v-chip>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="closeDialog">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import { useAuthStore } from '../stores/auth.js'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const projectStore = useProjectStore()
const authStore = useAuthStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  projectId: {
    type: String,
    default: null
  },
  currentProject: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'restored'])

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const loading = ref(false)
const history = ref([])
const currentIndex = ref(0)
const showComparison = ref(true)
const restoring = ref(false)
const showOnlyChanged = ref(false)
const hideEmpty = ref(false)

// Per-field restore staging: { [fieldName]: restoredValue }. The user picks a
// past value from each field's own timeline; selections accumulate here and are
// applied together on confirm.
const pendingRestores = ref({})
const pendingCount = computed(() => Object.keys(pendingRestores.value).length)
const isPending = (fieldName) => Object.prototype.hasOwnProperty.call(pendingRestores.value, fieldName)

// Check if user can restore (admin or superadmin)
const canRestore = computed(() => {
  const user = authStore.user
  const userRole = user?.role
  
  // Debug log
  console.log('ProjectHistory - Auth user:', user)
  console.log('ProjectHistory - User role:', userRole)
  console.log('ProjectHistory - Can restore:', userRole === 'admin' || userRole === 'superadmin')
  
  return userRole === 'admin' || userRole === 'superadmin'
})

// Current history entry being displayed
const currentHistoryEntry = computed(() => {
  if (!history.value || history.value.length === 0) return null
  return history.value[currentIndex.value]
})

// Previous history entry for comparison
const previousHistoryEntry = computed(() => {
  if (!history.value || history.value.length === 0 || currentIndex.value === 0) return null
  return history.value[currentIndex.value - 1]
})

// Project type of the currently displayed history entry
const currentProjectType = computed(() => {
  const pt = currentHistoryEntry.value?.data?.projectType
  return pt !== undefined && pt !== null && pt !== '' ? parseInt(pt) : null
})

// Fields to display (from fields.json configuration), filtered by the
// project type of the current entry so only relevant fields are shown.
const displayFields = computed(() => {
  if (!currentHistoryEntry.value?.data) return {}

  const fields = {}
  const fieldsConfig = projectStore.fields || []
  const ptId = currentProjectType.value

  if (Array.isArray(fieldsConfig)) {
    for (const field of fieldsConfig) {
      // Skip system fields we don't want to show in history
      if (['createdAt', 'createdBy', 'updatedAt', 'updatedBy'].includes(field.name)) {
        continue
      }
      // Only show fields relevant to this project type (also avoids duplicate
      // field names defined per-type overwriting each other)
      if (ptId !== null && Array.isArray(field.visibleInProjectTypes) &&
          !field.visibleInProjectTypes.includes(ptId)) {
        continue
      }
      fields[field.name] = field
    }
  }

  return fields
})

// Ordine delle card (gruppi): definito per tipo di processo in cfg.json
// tramite "groupOrder". Usato come fallback se non configurato.
const GROUP_ORDER_FALLBACK = ['Informações Básicas', 'Serviços', 'Transporte', 'Financeiro', 'Documentos', 'Observações']

// Group display fields by their "group" property, ordered like the form.
const fieldsByGroup = computed(() => {
  const groups = {}
  for (const [fieldName, field] of Object.entries(displayFields.value)) {
    const key = field.group ? field.group.description : 'Outros'
    if (!groups[key]) {
      groups[key] = {
        description: field.group?.description || 'Outros',
        color: field.group?.color || 'grey',
        fields: {}
      }
    }
    groups[key].fields[fieldName] = field
  }

  const ptInfo = projectStore.projectTypes.find(pt => String(pt.id) === String(currentProjectType.value))
  const order = (ptInfo && Array.isArray(ptInfo.groupOrder) && ptInfo.groupOrder.length > 0)
    ? ptInfo.groupOrder
    : GROUP_ORDER_FALLBACK
  const lastIndex = order.length
  const rankOf = (name) => {
    const i = order.indexOf(name)
    if (i !== -1) return i
    const obs = order.indexOf('Observações')
    return obs !== -1 ? obs - 0.5 : lastIndex
  }
  const ordered = {}
  Object.keys(groups)
    .sort((a, b) => rankOf(a) - rankOf(b))
    .forEach(k => { ordered[k] = groups[k] })

  return ordered
})

// Whether the "only changed" filter is currently meaningful/active
const onlyChangedActive = computed(() =>
  showOnlyChanged.value && showComparison.value && currentIndex.value > 0
)

// True when a field has no meaningful value in the current entry
const isEmptyValue = (fieldName) => {
  const v = getCurrentValue(fieldName)
  if (v === null || v === undefined || v === '') return true
  if (Array.isArray(v) && v.length === 0) return true
  return false
}

// Apply the "only changed" / "hide empty" filters to a group's fields
const filterGroupFields = (groupFields) => {
  const result = {}
  for (const [fieldName, field] of Object.entries(groupFields)) {
    if (onlyChangedActive.value && !isFieldChanged(fieldName)) continue
    if (hideEmpty.value && isEmptyValue(fieldName)) continue
    result[fieldName] = field
  }
  return result
}

// Groups with their fields filtered; empty groups are dropped
const visibleGroups = computed(() => {
  const result = {}
  for (const [key, group] of Object.entries(fieldsByGroup.value)) {
    const fields = filterGroupFields(group.fields)
    if (Object.keys(fields).length > 0) {
      result[key] = { ...group, fields }
    }
  }
  return result
})

// Get current value of a field
const getCurrentValue = (fieldName) => {
  if (!currentHistoryEntry.value?.data) return ''
  return currentHistoryEntry.value.data[fieldName] ?? ''
}

// Get previous value of a field
const getPreviousValue = (fieldName) => {
  if (!previousHistoryEntry.value?.data) return ''
  return previousHistoryEntry.value.data[fieldName] ?? ''
}

// Check if a field has changed
const isFieldChanged = (fieldName) => {
  if (currentIndex.value === 0) return false
  const currentValue = getCurrentValue(fieldName)
  const previousValue = getPreviousValue(fieldName)
  return JSON.stringify(currentValue) !== JSON.stringify(previousValue)
}

// List of changed fields
const changedFieldsList = computed(() => {
  if (currentIndex.value === 0) return []
  const changed = []
  for (const fieldName of Object.keys(displayFields.value)) {
    if (isFieldChanged(fieldName)) {
      changed.push(fieldName)
    }
  }
  return changed
})

// Count of changed fields
const changedFieldsCount = computed(() => changedFieldsList.value.length)

// Navigation methods
const navigateToPrevious = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const navigateToNext = () => {
  if (currentIndex.value < history.value.length - 1) {
    currentIndex.value++
  }
}

// Check if field is a system field that should not be restored
const isSystemField = (fieldName) => {
  return ['idProject', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy', 'version'].includes(fieldName)
}

// Live (latest) value of a field, i.e. its value on the current project version.
const getLiveValue = (fieldName) => {
  const entries = history.value || []
  if (entries.length === 0) return ''
  return entries[entries.length - 1]?.data?.[fieldName] ?? ''
}

// True when the field is empty on the live project version.
const isLiveEmpty = (fieldName) => {
  const v = getLiveValue(fieldName)
  if (v === null || v === undefined || v === '') return true
  if (Array.isArray(v) && v.length === 0) return true
  return false
}

// Can this field's timeline menu be opened? (admin only, not a system field)
// Shown when there is more than one past value to restore, or when the field is
// currently populated and can therefore be cleared to empty.
const canRestoreField = (fieldName) => {
  if (!canRestore.value || isSystemField(fieldName)) return false
  if (fieldValueHistory(fieldName).length > 1) return true
  return !isLiveEmpty(fieldName)
}

// Type-appropriate empty value used when clearing a field.
const emptyValueFor = (fieldName) => {
  const type = displayFields.value[fieldName]?.type
  if (type === 'notelist' || type === 'multiselect' || type === 'servicequantity') return []
  if (type === 'date' || type === 'datetime') return null
  return ''
}

// Stage clearing a field (set it to empty) as part of the pending restore.
const stageClear = (fieldName) => {
  stageRestore(fieldName, emptyValueFor(fieldName))
}

// True when the field is already staged to be cleared to empty.
const isClearStaged = (fieldName) => {
  if (!isPending(fieldName)) return false
  const v = pendingRestores.value[fieldName]
  if (v === null || v === undefined || v === '') return true
  if (Array.isArray(v) && v.length === 0) return true
  return false
}

// Build the distinct value timeline of a single field across all versions.
// Consecutive identical values are collapsed; returned newest-first.
const fieldValueHistory = (fieldName) => {
  const entries = history.value || []
  if (entries.length === 0) return []

  const liveKey = JSON.stringify(entries[entries.length - 1]?.data?.[fieldName] ?? '')
  const result = []
  let lastKey = null
  for (let i = 0; i < entries.length; i++) {
    const value = entries[i]?.data?.[fieldName] ?? ''
    const key = JSON.stringify(value)
    if (i > 0 && key === lastKey) continue
    lastKey = key
    result.push({
      value,
      timestamp: entries[i].timestamp,
      modifiedBy: entries[i].modifiedBy,
      isCurrent: key === liveKey
    })
  }
  return result.reverse()
}

// Stage / unstage a field value for restore
const stageRestore = (fieldName, value) => {
  pendingRestores.value = { ...pendingRestores.value, [fieldName]: value }
}
const unstageRestore = (fieldName) => {
  const next = { ...pendingRestores.value }
  delete next[fieldName]
  pendingRestores.value = next
}
const clearPending = () => {
  pendingRestores.value = {}
}

// Confirm and execute restore of all staged fields in a single update
const confirmRestore = async () => {
  if (pendingCount.value === 0) return

  try {
    restoring.value = true

    const restoreData = { ...pendingRestores.value }

    // Include version for concurrency check
    if (props.currentProject?.version !== undefined) {
      restoreData.version = props.currentProject.version
    }

    // Call update API
    await projectStore.updateProject(props.projectId, restoreData)

    // Emit success event
    emit('restored', { fields: Object.keys(pendingRestores.value) })

    // Close dialog
    closeDialog()

  } catch (error) {
    console.error('Error restoring fields:', error)
    // Concorrenza, gate di validazione e fase riservata ad altri profili hanno ognuno il proprio
    // messaggio: quello generico lasciava l'utente senza sapere perche il salvataggio e stato rifiutato
    const detail = error?.response?.data?.message || error?.message
    alert(detail ? t('projects.history.restoreError') + '\n\n' + detail : t('projects.history.restoreError'))
  } finally {
    restoring.value = false
  }
}

// Format field value for display
const formatFieldValue = (fieldName, value) => {
  if (value === null || value === undefined || value === '') {
    return t('projects.history.empty')
  }
  
  const fieldConfig = displayFields.value[fieldName]
  
  // Special handling for notes field (array)
  if (fieldConfig?.type === 'notelist' && Array.isArray(value)) {
    return `${value.length} ${value.length === 1 ? 'nota' : 'notas'}`
  }
  
  // Format based on field type
  if (fieldConfig?.type === 'date') {
    try {
      // Se la data è in formato YYYY-MM-DD, parsala come data locale
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number)
        const date = new Date(year, month - 1, day) // month è 0-indexed
        return date.toLocaleDateString('it-IT')
      }
      const date = new Date(value)
      return date.toLocaleDateString('it-IT')
    } catch {
      return value
    }
  }
  
  // Format select fields
  if (fieldConfig?.type === 'select') {
    if (fieldName === 'projectType') {
      const type = projectStore.projectTypes.find(t => String(t.id) === String(value))
      return type?.descr || value
    }
    if (fieldName === 'activeStatus') {
      const status = projectStore.statusList.find(s => String(s.id) === String(value))
      return status ? `${status.icon} ${status.descr}` : value
    }
    if (fieldConfig.options) {
      const option = fieldConfig.options.find(o => o.value === value)
      return option?.text || value
    }
  }
  
  return value
}

// Check if field is notes field
const isNotesField = (fieldName) => {
  const fieldConfig = displayFields.value[fieldName]
  return fieldConfig?.type === 'notelist'
}

// Get status color for notes
const getStatusColor = (statusId) => {
  if (!statusId) return 'grey'
  const status = projectStore.statusList.find(s => s.id === statusId)
  return status?.color || 'grey'
}

// Format note timestamp
const formatNoteDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Format date and time
const formatDateTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  // Usa formato locale senza conversioni UTC
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Get field label
const getFieldLabel = (fieldName) => {
  return t(`projects.fields.${fieldName}`)
}

// Load history when dialog opens
watch(() => props.modelValue, async (newValue) => {
  if (newValue && props.projectId) {
    await loadHistory()
  }
})

// Load history data
const loadHistory = async () => {
  if (!props.projectId) return
  
  try {
    loading.value = true
    const historyData = await projectStore.getProjectHistory(props.projectId)
    
    if (historyData && historyData.history) {
      // Sort history by timestamp (oldest first)
      const sortedHistory = [...historyData.history].sort((a, b) => {
        return new Date(a.timestamp) - new Date(b.timestamp)
      })
      
      // Add current project state as the most recent entry
      if (props.currentProject) {
        sortedHistory.push({
          timestamp: props.currentProject.updatedAt || new Date().toISOString(),
          modifiedBy: props.currentProject.updatedBy || props.currentProject.createdBy || 'unknown',
          data: props.currentProject
        })
      }
      
      history.value = sortedHistory
      
      // Start at the most recent (last) entry
      currentIndex.value = history.value.length - 1
    } else {
      history.value = []
    }
  } catch (error) {
    console.error('Error loading history:', error)
    history.value = []
  } finally {
    loading.value = false
  }
}

const closeDialog = () => {
  dialog.value = false
  history.value = []
  currentIndex.value = 0
  showComparison.value = true
  showOnlyChanged.value = false
  hideEmpty.value = false
  restoring.value = false
  pendingRestores.value = {}
}
</script>

<style scoped>
.history-field {
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: rgba(0, 0, 0, 0.02);
}

.field-changed {
  background-color: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.5);
  border-width: 2px;
}

.field-pending {
  background-color: rgba(255, 87, 34, 0.12);
  border-color: rgba(255, 87, 34, 0.8);
  border-width: 2px;
}

.old-value {
  opacity: 0.7;
}

.new-value {
  color: rgb(var(--v-theme-primary));
}

:deep(.v-slider-thumb) {
  width: 20px;
  height: 20px;
}

.notes-list {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.note-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.note-item:last-child {
  border-bottom: none;
}
</style>
